import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { addNewMenuItem, deleteMenuItem, printTableSlip, sendToKitchen, updateMenuItem } from "../../../../../services/table.services.js";
import { getUniqueID, handleServerValidations } from "../../../../../../../utils/commonfunction.js";
import { ORDER_TYPE } from "../utils/constants.ts";
import { getSplitItemKey } from "./splitItemIdentity.ts";
import type { CombinedOrderItem } from "./useOrderData.ts";
import type { CustomItemDraft, ServiceResponse, UseOrderMutationsProps, UseOrderMutationsReturn } from "./types.ts";

const trash = "../../../../../../assets/restaurant/images/trash.svg";

export const useOrderMutations = ({
  tableId,
  orderType,
  transactionData,
  orderItems,
  hasSplitPayment,
  guestCount,
  reloadOrderData,
  fireToast,
}: UseOrderMutationsProps): UseOrderMutationsReturn => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [updatingOrderItemKey, setUpdatingOrderItemKey] = useState<string | null>(null);
  const [isAddingCustomItem, setIsAddingCustomItem] = useState(false);
  const [isPrintingTableSlip, setIsPrintingTableSlip] = useState(false);

  const runOrderMutation = useCallback(
    async ({
      request,
      onSuccess,
      errorLabel,
      fallbackMessage,
      onFinally,
    }: {
      request: Promise<unknown>;
      onSuccess?: (response: ServiceResponse) => void;
      errorLabel: string;
      fallbackMessage: string;
      onFinally?: () => void;
    }): Promise<void> => {
      try {
        const response = await request;
        const res = response as ServiceResponse;
        if (res.success) {
          onSuccess?.(res);
          return;
        }

        fireToast("error", handleServerValidations(res));
      } catch (error: unknown) {
        console.error(errorLabel, error);
        fireToast("error", fallbackMessage);
      } finally {
        onFinally?.();
      }
    },
    [fireToast],
  );

  const handleDeleteItem = useCallback(
    async (item: CombinedOrderItem): Promise<void> => {
      const result = await Swal.fire({
        title: t("msg_are_you_sure"),
        text: t("delete_menu_item_text"),
        imageUrl: trash,
        showCancelButton: true,
        confirmButtonColor: "#f8821b",
        cancelButtonColor: "#6c757d",
        confirmButtonText: t("true_label_yes"),
        cancelButtonText: t("false_label_no"),
        reverseButtons: true,
        customClass: {
          popup: "swal-restaurant",
          confirmButton: "btn btn-danger col",
          cancelButton: "btn btn-secondary col",
        },
        allowOutsideClick: false,
      });

      if (!result.isConfirmed) return;

      const payload = {
        order_id: orderType === ORDER_TYPE.EDIT_SALES ? transactionData?.order_id : item.order_id,
        item,
        type: orderType === ORDER_TYPE.EDIT_SALES ? orderType : "edit-order",
        transaction_id: orderType === ORDER_TYPE.EDIT_SALES ? tableId : undefined,
      };

      await runOrderMutation({
        request: deleteMenuItem(payload),
        onSuccess: (res) => {
          if (res?.data?.order_deleted === true) {
            navigate("/cashier/dashboard");
            return;
          }

          reloadOrderData();
          fireToast("success", res.message);
        },
        errorLabel: "[deleteMenuItem]",
        fallbackMessage: "Something went wrong while deleting the item.",
      });
    },
    [fireToast, navigate, orderType, reloadOrderData, runOrderMutation, t, tableId, transactionData?.order_id],
  );

  const handleInlineDeleteItem = useCallback(
    (item: CombinedOrderItem): void => {
      if (hasSplitPayment) return;
      if (orderType === ORDER_TYPE.EDIT_SALES && orderItems.length <= 1) {
        fireToast("error", "This is the last item in your order and cannot be deleted.");
        return;
      }

      void handleDeleteItem(item);
    },
    [fireToast, handleDeleteItem, hasSplitPayment, orderItems.length, orderType],
  );

  const updateItemQuantity = useCallback(
    (item: CombinedOrderItem, quantity: number): void => {
      const itemKey = getSplitItemKey(item);
      setUpdatingOrderItemKey(itemKey);

      const payload = {
        order_id: orderType === ORDER_TYPE.EDIT_SALES ? transactionData?.order_id : item?.order_id,
        item: { ...item, temp_item_id: item?.temp_item_id, _id: item?._id, quantity },
        type: orderType === ORDER_TYPE.EDIT_SALES ? orderType : "edit-order",
        transaction_id: orderType === ORDER_TYPE.EDIT_SALES ? tableId : undefined,
      };

      void runOrderMutation({
        request: updateMenuItem(payload),
        onSuccess: () => {
          reloadOrderData();
        },
        errorLabel: "[updateMenuItem]",
        fallbackMessage: "Something went wrong while updating the quantity.",
        onFinally: () => setUpdatingOrderItemKey(null),
      });
    },
    [orderType, reloadOrderData, runOrderMutation, tableId, transactionData?.order_id],
  );

  const handleSendToKitchen = useCallback(
    async (): Promise<void> => {
      await runOrderMutation({
        request: sendToKitchen({ tableId }),
        onSuccess: (res) => {
          const sent = Number((res?.data as { sent?: number })?.sent || 0);
          reloadOrderData();
          // button is disabled when nothing is unsent, so sent===0 is only a rare
          // stale-state race: reload silently, no false "success" toast.
          if (sent > 0) {
            fireToast("success", t("label_sent_to_kitchen"));
          }
        },
        errorLabel: "[sendToKitchen]",
        fallbackMessage: "Something went wrong while sending to the kitchen.",
      });
    },
    [fireToast, reloadOrderData, runOrderMutation, t, tableId],
  );

  const handlePrintTableSlip = useCallback(
    async (): Promise<void> => {
      if (!tableId || isPrintingTableSlip) {
        return;
      }

      setIsPrintingTableSlip(true);
      await runOrderMutation({
        request: printTableSlip({ tableId }),
        onSuccess: (res) => {
          reloadOrderData();
          fireToast("success", res.message || t("label_table_slip_printed"));
        },
        errorLabel: "[printTableSlip]",
        fallbackMessage: "Something went wrong while printing the table slip.",
        onFinally: () => setIsPrintingTableSlip(false),
      });
    },
    [fireToast, isPrintingTableSlip, reloadOrderData, runOrderMutation, t, tableId],
  );

  const handleAddCustomItem = useCallback(
    async (draft: CustomItemDraft): Promise<boolean> => {
      setIsAddingCustomItem(true);

      const type = orderType === ORDER_TYPE.EDIT_SALES
        ? ORDER_TYPE.EDIT_SALES
        : orderItems.length > 0 ? ORDER_TYPE.EDIT_ORDER : ORDER_TYPE.NEW_ORDER;

      try {
        const response = await addNewMenuItem({
          table_id: tableId,
          transaction_id: orderType === ORDER_TYPE.EDIT_SALES ? tableId : undefined,
          type,
          // WRDS-1274: a custom item can be the line that OPENS the order, so
          // this request is the only moment the pre-menu gate's count can reach
          // the server — same rule as submitMenuItem in CashierMenu. New-order
          // path only; on edit-order the count is already on the order and
          // resending a stale one would overwrite it.
          ...(type === ORDER_TYPE.NEW_ORDER && guestCount
            ? { number_of_persons: guestCount }
            : {}),
          item: {
            ...draft,
            total_price: Number(draft.total_price),
            quantity: 1,
            is_custom_item: true,
            temp_item_id: getUniqueID("custom-item-"),
          },
        }) as unknown as ServiceResponse;

        if (!response.success) {
          fireToast("error", handleServerValidations(response));
          return false;
        }

        reloadOrderData();
        fireToast("success", response.message);
        return true;
      } catch (error: unknown) {
        console.error("[addCustomItem]", error);
        fireToast("error", t("custom_item_save_error"));
        return false;
      } finally {
        setIsAddingCustomItem(false);
      }
    },
    [fireToast, guestCount, orderItems.length, orderType, reloadOrderData, t, tableId],
  );

  const handleSetQuantity = useCallback(
    (item: CombinedOrderItem, quantity: number): void => {
      if (hasSplitPayment) return;
      if (quantity < 1) return;
      updateItemQuantity(item, quantity);
    },
    [hasSplitPayment, updateItemQuantity],
  );

  const handleDecreaseQuantity = useCallback(
    (item: CombinedOrderItem): void => {
      if (hasSplitPayment) return;
      const nextQuantity = Number(item?.quantity || 0) - 1;
      if (nextQuantity < 1) return;
      updateItemQuantity(item, nextQuantity);
    },
    [hasSplitPayment, updateItemQuantity],
  );

  const handleIncreaseQuantity = useCallback(
    (item: CombinedOrderItem): void => {
      if (hasSplitPayment) return;
      const nextQuantity = Number(item?.quantity || 0) + 1;
      updateItemQuantity(item, nextQuantity);
    },
    [hasSplitPayment, updateItemQuantity],
  );

  return {
    updatingOrderItemKey,
    isAddingCustomItem,
    handleInlineDeleteItem,
    handleSetQuantity,
    handleDecreaseQuantity,
    handleIncreaseQuantity,
    handleSendToKitchen,
    handlePrintTableSlip,
    isPrintingTableSlip,
    handleAddCustomItem,
  };
};
